import { useEffect, useState } from "react";
import { api, mediaUrl, uploadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ConfirmButton } from "@/components/ConfirmButton";
import { ImagePlus, KeyRound, Loader2, Globe, Lock, Users } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({ name: "", display_name: "", bio: "", location: "", avatar_url: "", collection_public: true, scroll_visible: true });
  const [secret, setSecret] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (user) setForm({ name: user.name || "", display_name: user.display_name || "", bio: user.bio || "", location: user.location || "", avatar_url: user.avatar_url || "", collection_public: user.collection_public !== false, scroll_visible: user.scroll_visible !== false });
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    await api.put("/auth/profile", form);
    await refreshUser();
    toast.success("Profile saved");
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setForm((f) => ({ ...f, avatar_url: url }));
      toast.success("Avatar uploaded — click Save to apply");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const redeem = async () => {
    try {
      const { data } = await api.post("/admin/redeem-secret", { word: secret });
      toast.success(data.message);
      setSecret("");
      await refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Incorrect secret word");
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-2xl mx-auto px-4 lg:px-8 py-10">
      <p className="font-tech text-xs uppercase tracking-widest brand-text">Account</p>
      <h1 className="font-display text-4xl font-bold mb-6">Your Profile</h1>

      <div className="surface border rounded-2xl p-6 space-y-5" data-testid="profile-form">
        <div className="flex items-center gap-5">
          <div className="w-24 h-24 rounded-full overflow-hidden grid place-items-center text-2xl font-bold shrink-0" style={{ background: "var(--honey-500)", color: "#3a2a10" }}>
            {form.avatar_url ? <img src={mediaUrl(form.avatar_url)} alt="" className="w-full h-full object-cover" /> : (user.username || "?")[0].toUpperCase()}
          </div>
          <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-full surface border text-sm font-semibold" data-testid="avatar-upload-label">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} Change avatar
            <input type="file" accept="image/*" className="hidden" onChange={uploadAvatar} data-testid="avatar-upload-input" />
          </label>
        </div>
        <div>
          <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Username</label>
          <input disabled value={user.username} className="w-full surface border rounded-lg px-3 py-2.5 opacity-60" />
        </div>
        <div>
          <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Display name</label>
          <input data-testid="profile-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="w-full surface border rounded-lg px-3 py-2.5" />
        </div>
        <div>
          <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Location</label>
          <input data-testid="profile-location-input" value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} className="w-full surface border rounded-lg px-3 py-2.5" />
        </div>
        <div>
          <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Bio</label>
          <textarea data-testid="profile-bio-input" value={form.bio} onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))} rows={3} className="w-full surface border rounded-lg px-3 py-2.5" />
        </div>
        <div className="border rounded-xl p-4" style={{ borderColor: "var(--page-border)" }}>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              {form.collection_public ? <Globe className="w-5 h-5 brand-text" /> : <Lock className="w-5 h-5 text-muted-page" />}
              <div>
                <p className="font-semibold">Collection visibility</p>
                <p className="text-xs text-muted-page">{form.collection_public ? "Public — other collectors can see your collection & map" : "Private — only you can see your collection & map"}</p>
              </div>
            </div>
            <button
              type="button"
              data-testid="collection-privacy-toggle"
              role="switch"
              aria-checked={form.collection_public}
              onClick={() => setForm((f) => ({ ...f, collection_public: !f.collection_public }))}
              className="relative w-14 h-8 rounded-full transition-colors shrink-0"
              style={{ background: form.collection_public ? "var(--honey-500)" : "var(--page-border)" }}
            >
              <span className="absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow transition-transform" style={{ transform: form.collection_public ? "translateX(24px)" : "translateX(0)" }} />
            </button>
          </div>
        </div>

        <div className="border rounded-xl p-4 space-y-4" style={{ borderColor: "var(--page-border)" }}>
          <div>
            <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Community scroll name</label>
            <input data-testid="profile-scroll-name-input" value={form.display_name} onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))} placeholder={form.name || user.username} className="w-full surface border rounded-lg px-3 py-2.5" />
            <p className="text-xs text-muted-page mt-1">A distinct name shown in the homepage collector scroll. Leave blank to use your display name.</p>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Users className={`w-5 h-5 ${form.scroll_visible ? "brand-text" : "text-muted-page"}`} />
              <div>
                <p className="font-semibold">Show me in the community scroll</p>
                <p className="text-xs text-muted-page">{form.scroll_visible ? "On — your name & badge scroll on the homepage" : "Off — you appear anonymously as \"New Member\""}</p>
              </div>
            </div>
            <button
              type="button"
              data-testid="scroll-visibility-toggle"
              role="switch"
              aria-checked={form.scroll_visible}
              onClick={() => setForm((f) => ({ ...f, scroll_visible: !f.scroll_visible }))}
              className="relative w-14 h-8 rounded-full transition-colors shrink-0"
              style={{ background: form.scroll_visible ? "var(--honey-500)" : "var(--page-border)" }}
            >
              <span className="absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow transition-transform" style={{ transform: form.scroll_visible ? "translateX(24px)" : "translateX(0)" }} />
            </button>
          </div>
        </div>
        <ConfirmButton testId="save-profile-btn" onConfirm={save} title="Save profile?" description="Save changes to your profile?" confirmLabel="Yes, save" className="px-6 py-3 rounded-full font-semibold" style={{ background: "var(--brand)", color: "#fff" }}>Save profile</ConfirmButton>
      </div>

      {user.role !== "owner" && user.role !== "admin" && (
        <div className="surface border rounded-2xl p-6 mt-6">
          <div className="flex items-center gap-2 mb-2"><KeyRound className="w-5 h-5 brand-text" /><h2 className="font-display text-xl font-bold">Have an admin code?</h2></div>
          <p className="text-sm text-muted-page mb-3">Enter the secret word to unlock administrator access.</p>
          <div className="flex gap-2">
            <input data-testid="secret-word-input" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="Secret word" className="flex-1 surface border rounded-lg px-3 py-2.5" />
            <button data-testid="redeem-secret-btn" onClick={redeem} className="px-5 rounded-lg font-semibold text-white" style={{ background: "var(--brand)" }}>Redeem</button>
          </div>
        </div>
      )}
      {(user.role === "admin" || user.role === "owner") && (
        <p className="mt-6 text-sm text-muted-page">You have <span className="font-tech font-bold brand-text uppercase">{user.role}</span> access.</p>
      )}
    </div>
  );
}
